import React, { useRef, useState, useEffect, useCallback } from "react";
import SubscriptionPlansSection from "../../../../subscription/components/SubscriptionPlansSection";
import SubscriptionLifecycleActions from "../../../../subscription/components/SubscriptionLifecycleActions";
import PaymentMethodSection from "../../../../subscription/components/PaymentMethodSection";
import UpdatePaymentMethodModal from "../../../../subscription/components/UpdatePaymentMethodModal";
import UpdateBillingAddressModal from "../../../../subscription/components/UpdateBillingAddressModal";
import FeatureComparison from "../../../../subscription/components/FeatureComparison";
import GlobalDiscountBanner from "../../../../subscription/components/GlobalDiscountBanner";
import {
  PAYMENT_HEALTH_STATES,
  PAYMENT_SECTION_PLACEMENTS,
  derivePaymentHealth,
  derivePaymentSectionPlacement,
} from "../../../../subscription/utils/paymentMethodHelpers";
import { apiFetch } from "../../../../core/utils/api";
import Modal from "../../../../design-system/components/modals/Modal";
import { PrimaryButton, SecondaryButton, DangerButton } from "../../../../design-system/components/buttons";
import { TextArea } from "../../../../design-system/components/inputs";
import { typography, surfaces, borders, textColors } from "../../../../design-system/tokens";
import SubscriptionPaymentModal from "../../../../subscription/components/SubscriptionPaymentModal";
import SubscriptionUnavailableModal from "../../../../subscription/components/SubscriptionUnavailableModal";
import { buildCouponStateFromPreview } from "../../../../subscription/utils/couponUtils";
import { clearReferralCoupon, getReferralCoupon, setReferralCoupon } from "../../../../core/utils/referralCoupon";
import { submitPotentialCustomerLead } from "../../../../core/utils/leadsApi";
import { getNewSubscriptionsAllowedSafe } from "../../../../core/utils/subscriptionConfigApi";
import ConfettiOverlay from "../../../../design-system/components/ConfettiOverlay";

const API_ENDPOINTS = {
  CURRENT_SUBSCRIPTION: '/api/subscription/current/',
  PLANS: '/api/subscription/plans/',
  STRIPE_SUBSCRIPTION_START: "/api/stripe/subscription/start/",
  STRIPE_CHANGE_NOW: "/api/stripe/subscription/change/",
  STRIPE_SCHEDULE: "/api/stripe/subscription/schedule/",
  RETENTION_CLAIM: "/api/stripe/subscription/retention-offer/claim/",
  COUPON_PREVIEW: "/api/coupons/preview/",
  FREE_ACCESS_COUPON_REDEEM: "/api/coupons/free-access/redeem/",
  GLOBAL_DISCOUNT_ACTIVE: "/api/global-discount/active/",
  // Self-service payment-method management.
  PAYMENT_METHOD_DETAILS: "/api/stripe/payment-method/",
  PAYMENT_METHOD_DELETE: "/api/stripe/payment-method/", // + `${pmId}/`
  BILLING_PORTAL: "/api/stripe/billing-portal/",
};

const MOCK_SUBSCRIPTION = {
  plan: "free",
  status: "active",
  current_period_end: "2024-12-31",
  will_renew: true,
};

const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
  </div>
);

const SubscriptionSettings = ({ username }) => {
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAnnualPricing, setShowAnnualPricing] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  // couponState stores preview results by billing period so UI can show discounts for both monthly and annual views.
  // Shape:
  // {
  //   code,
  //   include_renewals,
  //   additional_months,
  //   byPeriod: {
  //     monthly: { plans: [...] } | null,
  //     yearly: { plans: [...] } | null,
  //   }
  // }
  const [couponState, setCouponState] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelStage, setCancelStage] = useState("confirm"); // "offer" | "plus_billing_choice" | "confirm" | "done" | "retention"
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  // Tracks which Plus billing option ("monthly" | "yearly") the user just clicked
  // so the corresponding card can show its loading state independently.
  const [pendingPlusBillingPeriod, setPendingPlusBillingPeriod] = useState(null);
  const [cancelReason, setCancelReason] = useState(null); // string | 'other'
  const [cancelOtherText, setCancelOtherText] = useState("");
  const [cancelFeedbackLoading, setCancelFeedbackLoading] = useState(false);
  const [cancelFeedbackError, setCancelFeedbackError] = useState(null);
  const [cancelFeedbackSent, setCancelFeedbackSent] = useState(false);
  const [retentionLoading, setRetentionLoading] = useState(false);
  const [retentionError, setRetentionError] = useState(null);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeError, setResumeError] = useState(null);
  const [cancelScheduledChangeLoading, setCancelScheduledChangeLoading] = useState(false);
  const [cancelScheduledChangeError, setCancelScheduledChangeError] = useState(null);
  const [globalDiscount, setGlobalDiscount] = useState(null);
  const [globalDiscountLoading, setGlobalDiscountLoading] = useState(false);
  const [globalDiscountError, setGlobalDiscountError] = useState(null);
  const [checkoutNotice, setCheckoutNotice] = useState(null); // string | null
  const [subscriptionUnavailableModalOpen, setSubscriptionUnavailableModalOpen] = useState(false);
  // Stacking model: global discount (if eligible) always stacks with the entered coupon (if eligible).
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null); // { planName, billingPeriod, couponCode }
  const [accessCouponModalOpen, setAccessCouponModalOpen] = useState(false);
  const [accessCouponModalData, setAccessCouponModalData] = useState(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successPlan, setSuccessPlan] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const autoApplyAttemptedRef = useRef(false);

  // Payment-method management (read via /api/stripe/payment-method/).
  const [paymentMethodDetails, setPaymentMethodDetails] = useState(null);
  const [paymentMethodLoading, setPaymentMethodLoading] = useState(false);
  const [paymentMethodError, setPaymentMethodError] = useState(null);
  const [updatePmModalOpen, setUpdatePmModalOpen] = useState(false);
  const [updatePmIsRecovery, setUpdatePmIsRecovery] = useState(false);
  const [updateAddressModalOpen, setUpdateAddressModalOpen] = useState(false);
  const [removePmLoading, setRemovePmLoading] = useState(false);
  const [removePmError, setRemovePmError] = useState(null);
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const [billingPortalError, setBillingPortalError] = useState(null);
  const [paymentMethodNotice, setPaymentMethodNotice] = useState(null);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      
      const [subscriptionResponse, plansResponse] = await Promise.all([
        apiFetch(API_ENDPOINTS.CURRENT_SUBSCRIPTION, {
          headers: {
            'Content-Type': 'application/json',
          },
        }),
        apiFetch(API_ENDPOINTS.PLANS, {
          headers: {
            'Content-Type': 'application/json',
          },
        })
      ]);

      if (subscriptionResponse.ok) {
        const subscriptionData = await subscriptionResponse.json();
        setCurrentSubscription(subscriptionData);
      } else {
        setCurrentSubscription(MOCK_SUBSCRIPTION);
      }

      if (plansResponse.ok) {
        const plansData = await plansResponse.json();
        setPlans(plansData.plans);
      }
      
    } catch (error) {
      setCurrentSubscription(MOCK_SUBSCRIPTION);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const fetchPaymentMethodDetails = useCallback(async () => {
    setPaymentMethodLoading(true);
    setPaymentMethodError(null);
    try {
      const resp = await apiFetch(API_ENDPOINTS.PAYMENT_METHOD_DETAILS, {
        headers: { "Content-Type": "application/json" },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.error || "Could not load your payment method.");
      }
      setPaymentMethodDetails(data);
    } catch (e) {
      setPaymentMethodError(e?.message || "Could not load your payment method.");
    } finally {
      setPaymentMethodLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPaymentMethodDetails();
  }, [fetchPaymentMethodDetails]);

  const openUpdatePaymentMethodModal = useCallback((isRecovery = false) => {
    setRemovePmError(null);
    setUpdatePmIsRecovery(Boolean(isRecovery));
    setUpdatePmModalOpen(true);
  }, []);

  const handlePaymentMethodUpdated = useCallback(
    ({ retriedPastDue, retryFailed } = {}) => {
      setPaymentMethodNotice(
        retriedPastDue
          ? "Payment method updated and the failed invoice was retried successfully."
          : retryFailed
          ? "Card saved, but retrying the failed invoice didn't go through. Please contact support if the issue persists."
          : "Payment method updated."
      );
      // Refresh both PM details and subscription so UI banners (past_due → active) clear.
      fetchPaymentMethodDetails();
      fetchSubscriptionData();
    },
    [fetchPaymentMethodDetails]
  );

  const handleBillingAddressUpdated = useCallback(() => {
    setPaymentMethodNotice("Billing address updated. Tax on your next invoice will use the new address.");
    fetchPaymentMethodDetails();
  }, [fetchPaymentMethodDetails]);

  const handleRemovePaymentMethod = useCallback(async () => {
    const pmId = paymentMethodDetails?.default_payment_method?.stripe_payment_method_id;
    if (!pmId) return;
    if (typeof window !== "undefined") {
      const confirmed = window.confirm(
        "Remove this saved card? You'll need to add a new one before any future renewals can succeed."
      );
      if (!confirmed) return;
    }
    setRemovePmLoading(true);
    setRemovePmError(null);
    try {
      const resp = await apiFetch(`${API_ENDPOINTS.PAYMENT_METHOD_DELETE}${encodeURIComponent(pmId)}/`, {
        method: "DELETE",
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.error || "Could not remove this payment method.");
      }
      setPaymentMethodNotice("Payment method removed.");
      fetchPaymentMethodDetails();
    } catch (e) {
      setRemovePmError(e?.message || "Could not remove this payment method.");
    } finally {
      setRemovePmLoading(false);
    }
  }, [paymentMethodDetails, fetchPaymentMethodDetails]);

  const handleOpenBillingPortal = useCallback(async () => {
    setBillingPortalLoading(true);
    setBillingPortalError(null);
    try {
      const resp = await apiFetch(API_ENDPOINTS.BILLING_PORTAL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ return_url: window.location.href }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.error || "Could not open the billing portal.");
      }
      const url = String(data?.url || "").trim();
      if (!url) throw new Error("Stripe did not return a portal URL.");
      const win = window.open(url, "_blank");
      if (win) {
        try { win.opener = null; } catch (_) {}
        try { win.focus?.(); } catch (_) {}
      } else {
        window.location.assign(url);
      }
    } catch (e) {
      setBillingPortalError(e?.message || "Could not open the billing portal.");
    } finally {
      setBillingPortalLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setGlobalDiscountLoading(true);
      setGlobalDiscountError(null);
      try {
        const resp = await apiFetch(API_ENDPOINTS.GLOBAL_DISCOUNT_ACTIVE, {
          headers: { "Content-Type": "application/json" },
        });
        const data = await resp.json().catch(() => null);
        if (!resp.ok) throw new Error(data?.error || "Failed to load global discount.");
        if (!cancelled) setGlobalDiscount(data);
      } catch (e) {
        if (!cancelled) {
          setGlobalDiscount(null);
          setGlobalDiscountError(e?.message || "Failed to load global discount.");
        }
      } finally {
        if (!cancelled) setGlobalDiscountLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const currentPlan = plans.find(plan => plan.name === currentSubscription?.plan);
  const targetBillingPeriod = showAnnualPricing ? "yearly" : "monthly";
  const isAccessCouponApplied = Boolean(couponState?.is_access_coupon);
  const couponCode = !isAccessCouponApplied && couponState?.code ? couponState.code : null;
  const isPaidSubscription = (currentSubscription?.plan || "free") !== "free" && Number(currentSubscription?.price || 0) > 0;
  const accessGrant = currentSubscription?.access_grant || null;
  const hasAccessGrant = Boolean(accessGrant?.is_active);
  const shouldShowGlobalDiscount = !!globalDiscount && !(globalDiscount?.first_time_only && isPaidSubscription);
  const upcomingSubscription = Array.isArray(currentSubscription?.upcoming_subscriptions) && currentSubscription.upcoming_subscriptions.length > 0
    ? currentSubscription.upcoming_subscriptions[0]
    : null;

  const shouldRenderAccessCode = (raw) => {
    const code = String(raw || "").trim();
    if (!code) return false;
    const normalized = code.toLowerCase();
    // Backend/admin flows may represent an admin grant as a literal string.
    if (normalized === "(admin grant)" || normalized === "admin grant") return false;
    return true;
  };

  const formatDateShort = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  };

  const SadFaceIcon = ({ className = "" }) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
      className={className}
    >
      <circle cx="12" cy="12" r="9" strokeWidth="2" />
      <path d="M9 10h.01M15 10h.01" strokeWidth="3" strokeLinecap="round" />
      <path d="M9 16c1.5-1.3 4.5-1.3 6 0" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );

  const CANCELLATION_REASONS = [
    "I don't need it anymore",
    "It's too expensive",
    "Missing features I need",
    "Hard to use",
    "I'm switching to another tool",
  ];

  const submitCancellationFeedback = async ({ message }) => {
    const msg = String(message || "").trim();
    if (!msg) return;
    setCancelFeedbackLoading(true);
    setCancelFeedbackError(null);
    try {
      const resp = await apiFetch("/api/feedback/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          feedback: msg,
          situation: "CANCELLATION",
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.error || "Failed to send feedback.");
      setCancelFeedbackSent(true);
    } catch (e) {
      setCancelFeedbackError(e?.message || "Failed to send feedback.");
    } finally {
      setCancelFeedbackLoading(false);
    }
  };

  const getFriendlyCouponErrorMessage = (raw) => {
    const msg = String(raw || "").trim();
    const lower = msg.toLowerCase();

    if (!msg) return "We couldn’t apply that coupon. Please try again.";
    if (lower.includes("invalid coupon code")) {
      return "We couldn’t find that coupon code. Double-check it and try again.";
    }
    if (lower.includes("not available")) {
      return "That coupon has expired or was deactivated.";
    }
    if (lower.includes("only valid for first-time")) {
      return "That coupon is only valid for first-time subscribers.";
    }
    if (lower.includes("not valid for this billing period")) {
      return "That coupon doesn’t apply to this billing period.";
    }
    if (lower.includes("not valid for this plan")) {
      return "That coupon doesn’t apply to this plan.";
    }
    return msg;
  };

  const pickBestCouponError = (...errors) => {
    // Prioritize “expired/deactivated” over “not found” when multiple responses disagree.
    const normalized = (errors || []).map((e) => String(e || "").trim()).filter(Boolean);
    if (normalized.length === 0) return null;

    const lower = normalized.map((e) => e.toLowerCase());
    const idx = (predicate) => lower.findIndex(predicate);

    const iNotAvailable = idx((s) => s.includes("not available"));
    if (iNotAvailable !== -1) return normalized[iNotAvailable];

    const iFirstTime = idx((s) => s.includes("only valid for first-time"));
    if (iFirstTime !== -1) return normalized[iFirstTime];

    const iBilling = idx((s) => s.includes("not valid for this billing period"));
    if (iBilling !== -1) return normalized[iBilling];

    const iPlan = idx((s) => s.includes("not valid for this plan"));
    if (iPlan !== -1) return normalized[iPlan];

    const iInvalid = idx((s) => s.includes("invalid coupon code"));
    if (iInvalid !== -1) return normalized[iInvalid];

    return normalized[0];
  };

  const applyCoupon = async ({ code: codeOverride = null, silent = false } = {}) => {
    const code = (codeOverride || couponInput || "").trim();
    if (!code) return;
    setCouponLoading(true);
    if (!silent) setCouponError(null);
    try {
      // Free-access coupons (including access coupons granting free plan time) use their own endpoint.
      const freeAccessResp = await apiFetch(API_ENDPOINTS.FREE_ACCESS_COUPON_REDEEM, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      const freeAccessData = await freeAccessResp.json().catch(() => ({}));
      if (freeAccessResp.ok) {
        await fetchSubscriptionData();
        setCouponState({
          code,
          is_access_coupon: true,
          include_renewals: null,
          free_trial_days: 0,
          byPeriod: { monthly: null, yearly: null },
        });
        const grant = freeAccessData?.access_grant || null;
        // The free-access grant reports `ends_at`; the access modal displays `expires_at`.
        setAccessCouponModalData(grant ? { ...grant, expires_at: grant.expires_at ?? grant.ends_at } : { code });
        setAccessCouponModalOpen(true);
        // Access coupons can be linked to a regular coupon; store it so it
        // auto-applies at checkout later (same idea as a ref_coupon signup).
        if (grant?.linked_coupon_code) setReferralCoupon({ code: grant.linked_coupon_code });
        return;
      }
      const freeAccessError = freeAccessData?.error || "Failed to redeem coupon.";
      const freeAccessErrorLower = String(freeAccessError || "").toLowerCase();
      const allowFreeAccessFallback =
        freeAccessErrorLower.includes("coupon does not grant free access") ||
        freeAccessErrorLower.includes("invalid coupon code");
      if (!allowFreeAccessFallback) {
        throw new Error(freeAccessError);
      }

      const previewFor = async (billingPeriod) => {
        const resp = await apiFetch(API_ENDPOINTS.COUPON_PREVIEW, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, billing_period: billingPeriod }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data?.error || "Failed to validate coupon.");
        if (!data.valid) return { valid: false, error: data?.error || "Coupon is not available." };
        return { valid: true, data };
      };

      // Fetch both periods so switching Monthly/Annual keeps discounts consistent.
      const [monthly, yearly] = await Promise.all([previewFor("monthly"), previewFor("yearly")]);
      const monthlyValid = !!monthly?.valid;
      const yearlyValid = !!yearly?.valid;
      if (!monthlyValid && !yearlyValid) {
        const raw = pickBestCouponError(monthly?.error, yearly?.error);
        throw new Error(getFriendlyCouponErrorMessage(raw));
      }

      setCouponState(buildCouponStateFromPreview({ monthly, yearly }));
    } catch (e) {
      setCouponState(null);
      if (!silent) setCouponError(getFriendlyCouponErrorMessage(e?.message));
    } finally {
      setCouponLoading(false);
    }
  };

  const clearCoupon = () => {
    setCouponInput("");
    setCouponState(null);
    setCouponError(null);
    setCouponOpen(false);
    clearReferralCoupon();
  };

  // Auto-apply referral coupon (silent) once per page load. Keep it persisted until user clears it.
  useEffect(() => {
    if (autoApplyAttemptedRef.current) return;
    if (couponState?.code) return;
    const ref = getReferralCoupon();
    if (!ref?.code) return;

    autoApplyAttemptedRef.current = true;
    setCouponInput(ref.code);
    applyCoupon({ code: ref.code, silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponState?.code]);

  const getGlobalDiscountForPlan = (plan, billingPeriod) => {
    if (!shouldShowGlobalDiscount) return null;
    const period = billingPeriod || targetBillingPeriod;
    const allowedPlans = Array.isArray(globalDiscount?.plans) ? globalDiscount.plans : [];
    const allowedPeriods = Array.isArray(globalDiscount?.billing_periods) ? globalDiscount.billing_periods : [];
    if (allowedPlans.length > 0 && !allowedPlans.includes(plan.name)) return null;
    if (allowedPeriods.length > 0 && !allowedPeriods.includes(period)) return null;
    return {
      source: "global",
      name: globalDiscount?.name || "Global discount",
      percent_off: globalDiscount?.percent_off ?? null,
      amount_off: globalDiscount?.amount_off ?? null,
      free_trial_days: Number(globalDiscount?.free_trial_days || 0),
      include_renewals: globalDiscount?.include_renewals ?? null,
    };
  };

  const getCouponDiscountForPlan = (plan, billingPeriod) => {
    if (!couponState?.code || couponState?.is_access_coupon) return null;
    const period = billingPeriod || targetBillingPeriod;
    const source = couponState?.byPeriod?.[period];
    const match = (source?.plans || []).find((p) => p.plan === plan.name && p.billing_period === period);
    if (!match) return null;
    return {
      source: "coupon",
      name: couponState?.code || "Coupon",
      percent_off: couponState?.percent_off ?? null,
      amount_off: couponState?.amount_off ?? null,
      free_trial_days: Number(couponState?.free_trial_days || 0),
      include_renewals: couponState?.include_renewals ?? null,
    };
  };

  const pickDisplayDiscount = (plan, billingPeriod) => {
    const period = billingPeriod || targetBillingPeriod;
    const discounts = [];
    const gd = getGlobalDiscountForPlan(plan, period);
    const cd = getCouponDiscountForPlan(plan, period);
    if (gd) discounts.push(gd);
    if (cd) discounts.push(cd);
    if (!discounts.length) return null;
    return { discounts };
  };

  const formatFreeTrialLabel = (daysRaw) => {
    const days = Number(daysRaw || 0);
    if (!Number.isFinite(days) || days <= 0) return null;

    // Rule:
    // - If days < 29 and multiple of 7 -> show weeks
    // - If multiple of 30 -> show months
    // - Otherwise -> show days
    if (days < 29 && days % 7 === 0) {
      const weeks = days / 7;
      return `Free trial: ${weeks} ${weeks === 1 ? "week" : "weeks"}`;
    }
    if (days % 30 === 0) {
      const months = days / 30;
      return `Free trial: ${months} ${months === 1 ? "month" : "months"}`;
    }
    return `Free trial: ${days} ${days === 1 ? "day" : "days"}`;
  };

  const getCouponAppliedLabel = () => {
    if (!couponState?.code) return null;
    if (couponState?.is_access_coupon) {
      return `Access coupon applied · ${couponState.code}`;
    }
    const hasMonthly = !!couponState?.byPeriod?.monthly;
    const hasYearly = !!couponState?.byPeriod?.yearly;
    const periodLabel = hasMonthly && hasYearly ? "monthly + annual" : hasMonthly ? "monthly" : "annual";
    const trial = couponState?.free_trial_days ? ` · ${formatFreeTrialLabel(couponState.free_trial_days)}` : "";
    return `Applied ${couponState.code} · ${periodLabel}${trial}`;
  };

  const openInNewTab = (url) => {
    // Best-effort: open in a new tab AND try to focus it.
    // Note: using "noopener/noreferrer" can prevent access to the window handle (can't call focus),
    // so we open normally and then immediately sever the opener link for safety.
    const win = window.open(url, "_blank");
    if (win) {
      try {
        win.opener = null;
      } catch {
        // ignore
      }
      try {
        win.focus?.();
      } catch {
        // ignore
      }
      return;
    }

    // Pop-up blockers: fall back to same-tab navigation.
    window.location.assign(url);
  };

  const captureSubscriptionUpgradeInterest = ({ planName, billingPeriod }) => {
    const info = [
      "source=subscription_settings",
      username ? `username=${String(username)}` : null,
      planName ? `target_plan=${String(planName)}` : null,
      billingPeriod ? `billing_period=${String(billingPeriod)}` : null,
    ]
      .filter(Boolean)
      .join("; ");

    // Fire-and-forget: do not block UX on lead creation.
    try {
      void submitPotentialCustomerLead({
        situation: "SUBSCRIPTION_UPGRADE_INTEREST",
        info,
      });
    } catch {
      // ignore
    }
  };

  const buildCheckoutNotice = (discountApplied) => {
    const applied = discountApplied || null;
    if (!applied?.type || !applied?.name) return null;

    const rejectedName = applied?.rejected?.name ? String(applied.rejected.name) : null;
    if (rejectedName) {
      return `We applied "${applied.name}" instead of "${rejectedName}".`;
    }

    if (applied?.type === "stacked") return `Discounts applied: ${applied.name}.`;
    if (applied?.type === "global") return `"${applied.name}" applied.`;
    if (applied?.type === "coupon") return `Coupon "${applied.name}" applied.`;
    return `"${applied.name}" applied.`;
  };

  const handleCancelAtRenewal = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      const resp = await apiFetch(API_ENDPOINTS.STRIPE_SCHEDULE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "cancel" }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.error || "Failed to schedule cancellation.");
      await fetchSubscriptionData();
    } catch (e) {
      setActionError(e?.message || "Failed to schedule cancellation.");
    } finally {
      setActionLoading(false);
    }
  };

  const openCancelModal = () => {
    setCancelError(null);
    const isPro = (currentSubscription?.plan || "").toLowerCase() === "pro";
    // Don't re-pitch the "switch to Plus" offer to a Pro user who already
    // scheduled a switch to Plus — that's how they ended up here. Skip
    // straight to the cancellation confirm stage.
    const alreadySwitching = Boolean(upcomingSubscription);
    setCancelStage(isPro && !alreadySwitching ? "offer" : "confirm");
    setCancelModalOpen(true);
    setCancelReason(null);
    setCancelOtherText("");
    setCancelFeedbackError(null);
    setCancelFeedbackSent(false);
    setPendingPlusBillingPeriod(null);
  };

  const confirmCancelAtRenewal = async () => {
    setCancelLoading(true);
    setCancelError(null);
    try {
      const resp = await apiFetch(API_ENDPOINTS.STRIPE_SCHEDULE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "cancel" }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.error || "Failed to schedule cancellation.");
      await fetchSubscriptionData();
      setCancelStage("done");
    } catch (e) {
      setCancelError(e?.message || "Failed to schedule cancellation.");
    } finally {
      setCancelLoading(false);
    }
  };

  const handleDowngradeToPlus = async (billingPeriod) => {
    const period = billingPeriod === "yearly" ? "yearly" : "monthly";
    setCancelLoading(true);
    setPendingPlusBillingPeriod(period);
    setCancelError(null);
    try {
      const resp = await apiFetch(API_ENDPOINTS.STRIPE_SCHEDULE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "downgrade",
          plan_name: "plus",
          billing_period: period,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.error || "Failed to schedule downgrade.");
      await fetchSubscriptionData();
      setCancelModalOpen(false);
    } catch (e) {
      setCancelError(e?.message || "Failed to schedule downgrade.");
    } finally {
      setCancelLoading(false);
      setPendingPlusBillingPeriod(null);
    }
  };

  const resumeRenewal = async () => {
    setResumeLoading(true);
    setResumeError(null);
    try {
      const resp = await apiFetch(API_ENDPOINTS.STRIPE_SCHEDULE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "resume" }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.error || "Failed to resume subscription renewal.");
      await fetchSubscriptionData();
    } catch (e) {
      setResumeError(e?.message || "Failed to resume subscription renewal.");
    } finally {
      setResumeLoading(false);
    }
  };

  const cancelScheduledChange = async () => {
    setCancelScheduledChangeLoading(true);
    setCancelScheduledChangeError(null);
    try {
      const resp = await apiFetch(API_ENDPOINTS.STRIPE_SCHEDULE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "cancel_scheduled_change" }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.error || "Failed to keep your current plan.");
      await fetchSubscriptionData();
    } catch (e) {
      setCancelScheduledChangeError(e?.message || "Failed to keep your current plan.");
    } finally {
      setCancelScheduledChangeLoading(false);
    }
  };
  const claimRetentionOffer = async () => {
    setRetentionLoading(true);
    setRetentionError(null);
    try {
      const resp = await apiFetch(API_ENDPOINTS.RETENTION_CLAIM, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.error || "Failed to claim offer.");
      await fetchSubscriptionData();
      setCancelModalOpen(false);
    } catch (e) {
      setRetentionError(e?.message || "Failed to claim offer.");
    } finally {
      setRetentionLoading(false);
    }
  };

  const handleSelectPlan = async (plan) => {
    setActionLoading(true);
    setActionError(null);
    setCheckoutNotice(null);
    try {
      const allowed = await getNewSubscriptionsAllowedSafe();
      if (!allowed) {
        captureSubscriptionUpgradeInterest({ planName: plan?.name, billingPeriod: targetBillingPeriod });
        // Close any in-flight payment modal and show the friendly availability modal instead.
        setPaymentModalOpen(false);
        setPaymentTarget(null);
        setSubscriptionUnavailableModalOpen(true);
        return;
      }

      const returnUrl = window.location.href;
      const isFree = (currentSubscription?.plan || "free") === "free" || hasAccessGrant;
      const currentOrder = Number(currentPlan?.order ?? 0);
      const targetOrder = Number(plan?.order ?? 0);

      // Free -> paid: in-app payment modal using Stripe Elements (no Checkout redirect)
      if (isFree) {
        setPaymentTarget({ planName: plan.name, billingPeriod: targetBillingPeriod, couponCode });
        setPaymentModalOpen(true);
        return;
      }

      // Downgrades are scheduled for next renewal
      if (targetOrder < currentOrder) {
        throw new Error("Downgrades aren’t available here.");
      }

      // Upgrade now (Stripe may return a hosted invoice URL)
      const resp = await apiFetch(API_ENDPOINTS.STRIPE_CHANGE_NOW, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan_name: plan.name,
          billing_period: targetBillingPeriod,
          coupon_code: couponCode,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.error || "Failed to change plan.");
      if (data?.discount_applied) {
        const msg = buildCheckoutNotice(data.discount_applied);
        if (msg) setCheckoutNotice(msg);
      }
      if (data?.url) openInNewTab(data.url);
      await fetchSubscriptionData();
    } catch (e) {
      setActionError(e?.message || "Failed to change plan.");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6 sm:space-y-8 max-w-full overflow-hidden">
      <SubscriptionPaymentModal
        isOpen={paymentModalOpen}
        onClose={() => {
          setPaymentModalOpen(false);
          setPaymentTarget(null);
        }}
        planName={paymentTarget?.planName}
        billingPeriod={paymentTarget?.billingPeriod}
        couponCode={paymentTarget?.couponCode}
        onSubscriptionUpdated={(sub) => {
          setCurrentSubscription(sub);
        }}
        onActivated={({ planName: pn, billingPeriod: bp } = {}) => {
          setPaymentModalOpen(false);
          setPaymentTarget(null);
          setSuccessPlan({ planName: pn, billingPeriod: bp });
          setSuccessModalOpen(true);
          setShowConfetti(true);
          fetchSubscriptionData();
        }}
      />

      <SubscriptionUnavailableModal
        isOpen={subscriptionUnavailableModalOpen}
        onClose={() => {
          setSubscriptionUnavailableModalOpen(false);
        }}
      />

      {checkoutNotice && (
        <div className="rounded-lg border border-blue-200 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/20 px-4 py-3 text-sm text-blue-800 dark:text-blue-200">
          {checkoutNotice}
        </div>
      )}
      {actionError && (
        <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          {actionError}
        </div>
      )}

      <Modal
        isOpen={accessCouponModalOpen}
        onClose={() => {
          setAccessCouponModalOpen(false);
          setAccessCouponModalData(null);
        }}
        title="Access coupon applied"
        size="sm"
        closeOnOverlayClick
        closeOnEsc
        showCloseButton
        footer={
          <PrimaryButton
            onClick={() => {
              setAccessCouponModalOpen(false);
              setAccessCouponModalData(null);
            }}
          >
            Got it
          </PrimaryButton>
        }
      >
        <div className="space-y-3">
          <div className={`${typography.bodySmall} ${textColors.secondary}`}>
            You now have access to{" "}
            <span className={`${textColors.primary} font-medium`}>
              {(accessCouponModalData?.plan_name || accessGrant?.plan_name || "").toUpperCase() || "your plan"}
            </span>{" "}
            without adding a payment method.
          </div>
          <div className={`${typography.bodySmall} ${textColors.secondary}`}>
            Access ends on{" "}
            <span className={`${textColors.primary} font-medium`}>
              {formatDateShort(accessCouponModalData?.expires_at || accessGrant?.expires_at) || "—"}
            </span>
            .
          </div>
          {accessCouponModalData?.code ? (
            shouldRenderAccessCode(accessCouponModalData?.code) ? (
              <div className={`${typography.caption} ${textColors.tertiary}`}>Code: {accessCouponModalData.code}</div>
            ) : null
          ) : null}
          <div className={`${typography.caption} ${textColors.tertiary}`}>
            You can still switch to a paid plan anytime to keep access after it ends.
          </div>
        </div>
      </Modal>

      {paymentMethodNotice && (
        <div
          role="status"
          className="rounded-lg border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-200 flex items-start justify-between gap-3"
        >
          <span>{paymentMethodNotice}</span>
          <button
            type="button"
            aria-label="Dismiss notice"
            onClick={() => setPaymentMethodNotice(null)}
            className="text-emerald-700 dark:text-emerald-200 underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {(() => {
        const placement = derivePaymentSectionPlacement({
          hasStripeCustomer: !!paymentMethodDetails?.has_stripe_customer,
          hasActivePaidSubscription: !!paymentMethodDetails?.has_active_paid_subscription,
          savedPaymentMethods: paymentMethodDetails?.saved_payment_methods,
        });
        if (placement !== PAYMENT_SECTION_PLACEMENTS.ABOVE) return null;
        return (
          <PaymentMethodSection
            paymentMethodDetails={paymentMethodDetails}
            loading={paymentMethodLoading}
            error={paymentMethodError}
            onUpdatePaymentMethod={() => {
              const health = derivePaymentHealth({
                paymentMethod: paymentMethodDetails?.default_payment_method || null,
                hasActivePaidSubscription: !!paymentMethodDetails?.has_active_paid_subscription,
                subscriptionStatus: paymentMethodDetails?.subscription_status || "",
              });
              openUpdatePaymentMethodModal(health === PAYMENT_HEALTH_STATES.PAST_DUE);
            }}
            onRemovePaymentMethod={handleRemovePaymentMethod}
            removeLoading={removePmLoading}
            removeError={removePmError}
            onUpdateBillingAddress={() => setUpdateAddressModalOpen(true)}
            onOpenBillingPortal={handleOpenBillingPortal}
            portalLoading={billingPortalLoading}
            portalError={billingPortalError}
          />
        );
      })()}

      {/* Current Subscription */}
      <SubscriptionPlansSection
        currentSubscription={currentSubscription}
        currentPlan={currentPlan}
        currentSubscriptionFirst
        plans={plans}
        showAnnualPricing={showAnnualPricing}
        setShowAnnualPricing={setShowAnnualPricing}
        onSelectPlan={handleSelectPlan}
        upcomingSubscription={upcomingSubscription}
        banner={
          shouldShowGlobalDiscount ? (
            <GlobalDiscountBanner
              discount={globalDiscount}
              targetBillingPeriod={targetBillingPeriod}
              currentSubscription={currentSubscription}
            />
          ) : null
        }
        couponUI={{
          isOpen: couponOpen,
          toggleLink: () => setCouponOpen(!couponOpen),
          value: couponInput,
          setValue: (v) => {
            setCouponInput(v);
            if (couponError) setCouponError(null);
          },
          apply: applyCoupon,
          clear: couponState ? clearCoupon : null,
          loading: couponLoading,
          message: getCouponAppliedLabel(),
          error: couponError,
          getDisplayPrice: (plan, billingPeriod) => pickDisplayDiscount(plan, billingPeriod),
          couponCode: couponState?.code || null,
          hasMonthly: !!couponState?.byPeriod?.monthly,
          hasYearly: !!couponState?.byPeriod?.yearly,
          renewalApplies: couponState?.is_access_coupon ? null : couponState?.include_renewals ?? null,
          freeTrialLabel:
            couponState?.is_access_coupon ? null : couponState?.free_trial_days ? formatFreeTrialLabel(couponState.free_trial_days) : null,
        }}
        accessGrant={accessGrant}
      />

      {/* Subscription management actions (cancel / resume / keep-current).
          Driven by the lifecycle state machine — the component itself decides
          which actions to show based on the subscription state. */}
      <SubscriptionLifecycleActions
        subscription={currentSubscription}
        upcomingSubscription={upcomingSubscription}
        currentPlan={currentPlan}
        onCancelSubscription={openCancelModal}
        cancelSubscriptionLoading={cancelLoading}
        onResumeRenewal={resumeRenewal}
        resumeRenewalLoading={resumeLoading}
        resumeRenewalError={resumeError}
        onCancelScheduledChange={cancelScheduledChange}
        cancelScheduledChangeLoading={cancelScheduledChangeLoading}
        cancelScheduledChangeError={cancelScheduledChangeError}
      />

      {(() => {
        const placement = derivePaymentSectionPlacement({
          hasStripeCustomer: !!paymentMethodDetails?.has_stripe_customer,
          hasActivePaidSubscription: !!paymentMethodDetails?.has_active_paid_subscription,
          savedPaymentMethods: paymentMethodDetails?.saved_payment_methods,
        });
        if (placement !== PAYMENT_SECTION_PLACEMENTS.BELOW) return null;
        // Free users with saved cards: still let them update / remove / view invoices,
        // but no past-due state is possible (they have no active paid subscription).
        return (
          <PaymentMethodSection
            paymentMethodDetails={paymentMethodDetails}
            loading={paymentMethodLoading}
            error={paymentMethodError}
            onUpdatePaymentMethod={() => openUpdatePaymentMethodModal(false)}
            onRemovePaymentMethod={handleRemovePaymentMethod}
            removeLoading={removePmLoading}
            removeError={removePmError}
            onUpdateBillingAddress={() => setUpdateAddressModalOpen(true)}
            onOpenBillingPortal={handleOpenBillingPortal}
            portalLoading={billingPortalLoading}
            portalError={billingPortalError}
          />
        );
      })()}

      <UpdatePaymentMethodModal
        isOpen={updatePmModalOpen}
        onClose={() => setUpdatePmModalOpen(false)}
        onUpdated={handlePaymentMethodUpdated}
        isPastDueRecovery={updatePmIsRecovery}
      />

      <UpdateBillingAddressModal
        isOpen={updateAddressModalOpen}
        onClose={() => setUpdateAddressModalOpen(false)}
        currentAddress={paymentMethodDetails?.billing_address || null}
        onUpdated={handleBillingAddressUpdated}
      />

      <Modal
        isOpen={cancelModalOpen}
        onClose={() => {
          if (!cancelLoading && !retentionLoading) setCancelModalOpen(false);
        }}
        title={cancelStage === "retention" ? "We really don\u2019t want to see you go" : cancelStage === "offer" ? "Before you go\u2026" : cancelStage === "plus_billing_choice" ? "Choose your Plus billing" : cancelStage === "done" ? "Cancellation scheduled" : "Cancel subscription"}
        size="sm"
        closeOnOverlayClick={!cancelLoading && !retentionLoading}
        closeOnEsc={!cancelLoading && !retentionLoading}
        showCloseButton={!cancelLoading && !retentionLoading}
        footer={
          cancelStage === "retention" ? (
            <div className="flex flex-col w-full gap-2">
              <PrimaryButton onClick={claimRetentionOffer} loading={retentionLoading} className="w-full">
                Claim 30% off &amp; keep my subscription
              </PrimaryButton>
              <button
                type="button"
                disabled={retentionLoading}
                onClick={() => setCancelModalOpen(false)}
                className={`${typography.bodySmall} ${textColors.tertiary} self-center pt-1`}
              >
                No thanks
              </button>
            </div>
          ) : cancelStage === "done" ? (
            null
          ) : cancelStage === "offer" ? (
            <div className="flex flex-col w-full gap-2">
              <div className="flex gap-2 w-full">
                <SecondaryButton onClick={() => setCancelModalOpen(false)} disabled={cancelLoading} className="flex-1">
                  Keep Pro
                </SecondaryButton>
                <PrimaryButton
                  onClick={() => {
                    setCancelError(null);
                    setCancelStage("plus_billing_choice");
                  }}
                  disabled={cancelLoading}
                  className="flex-1"
                >
                  Switch to Plus
                </PrimaryButton>
              </div>
              <button
                type="button"
                disabled={cancelLoading}
                onClick={() => setCancelStage("confirm")}
                className={`${typography.bodySmall} text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 underline self-center pt-1`}
              >
                I still want to cancel
              </button>
            </div>
          ) : cancelStage === "plus_billing_choice" ? (
            <div className="flex flex-col w-full gap-2">
              <SecondaryButton
                onClick={() => {
                  setCancelError(null);
                  setCancelStage("offer");
                }}
                disabled={cancelLoading}
                className="w-full"
              >
                Back
              </SecondaryButton>
            </div>
          ) : (
            <>
              <SecondaryButton onClick={() => setCancelModalOpen(false)} disabled={cancelLoading}>
                Keep subscription
              </SecondaryButton>
              <DangerButton onClick={confirmCancelAtRenewal} loading={cancelLoading}>
                Yes, cancel
              </DangerButton>
            </>
          )
        }
      >
        {cancelStage === "retention" ? (
          <div className="space-y-5">
            <div className="flex justify-center">
              <div className="w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <svg className="w-7 h-7 text-amber-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="text-center space-y-2">
              <p className={`${typography.body} ${textColors.secondary}`}>
                As a thank you for being a subscriber, here&rsquo;s an exclusive one-time offer:
              </p>
            </div>
            <div className={`rounded-xl border ${borders.default} ${surfaces.secondary} p-5 text-center space-y-3`}>
              <div className={`${typography.h2} text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-sky-500`}>
                30% OFF
              </div>
              <p className={`${typography.body} ${textColors.primary} font-medium`}>
                your next billing cycle
              </p>
              <div className="flex items-center justify-center gap-4">
                <span className={`inline-flex items-center gap-1.5 ${typography.bodySmall} ${textColors.secondary}`}>
                  <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  7 days free trial
                </span>
                <span className={`inline-flex items-center gap-1.5 ${typography.bodySmall} ${textColors.secondary}`}>
                  <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Single-use discount
                </span>
              </div>
            </div>
            <p className={`${typography.bodySmall} ${textColors.tertiary} text-center`}>
              Click below to resume your subscription with 30% off applied automatically.
            </p>
            {retentionError && (
              <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-3 py-2">
                <p className={`${typography.bodySmall} text-red-700 dark:text-red-300`}>{retentionError}</p>
              </div>
            )}
          </div>
        ) : cancelStage === "offer" ? (() => {
          const plusPlan = plans.find(p => p.name === "plus");
          const monthlyPrice = Number(plusPlan?.monthly_price || 0).toFixed(2);
          const yearlyPrice = Number(plusPlan?.yearly_price || 0).toFixed(2);
          return (
            <div className="space-y-4">
              {cancelError && (
                <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-3 py-2">
                  <p className={`${typography.bodySmall} text-red-700 dark:text-red-300`}>{cancelError}</p>
                </div>
              )}

              <p className={`${typography.body} ${textColors.secondary}`}>
                Before you cancel, consider switching to our <span className={`${textColors.primary} font-semibold`}>Plus</span> plan — keep the core features at a lower price.
              </p>

              <div className={`rounded-xl border ${borders.default} ${surfaces.secondary} p-4 space-y-3`}>
                <div className={`${typography.h5} ${textColors.primary}`}>
                  {plusPlan?.display_name || "Plus"} plan
                </div>
                <div className="flex items-baseline gap-3">
                  <div>
                    <span className={`${typography.h3} ${textColors.primary}`}>${monthlyPrice}</span>
                    <span className={`${typography.bodySmall} ${textColors.muted}`}>/month</span>
                  </div>
                  <span className={`${typography.bodySmall} ${textColors.tertiary}`}>or</span>
                  <div>
                    <span className={`${typography.h3} ${textColors.primary}`}>${yearlyPrice}</span>
                    <span className={`${typography.bodySmall} ${textColors.muted}`}>/year</span>
                  </div>
                </div>
                {plusPlan?.highlights?.length ? (
                  <ul className="space-y-1.5">
                    {plusPlan.highlights.map((h, i) => (
                      <li key={i} className={`flex items-center ${typography.bodySmall} ${textColors.secondary}`}>
                        <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        {h}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>

              <p className={`${typography.bodySmall} ${textColors.tertiary}`}>
                The switch takes effect at your next billing cycle — you keep Pro until then.
              </p>
            </div>
          );
        })() : cancelStage === "plus_billing_choice" ? (() => {
          const plusPlan = plans.find(p => p.name === "plus");
          const monthlyPrice = Number(plusPlan?.monthly_price || 0);
          const yearlyPrice = Number(plusPlan?.yearly_price || 0);
          const yearlyMonthlyEquivalent = yearlyPrice > 0 ? yearlyPrice / 12 : 0;
          const yearlySavings = monthlyPrice > 0 && yearlyPrice > 0
            ? (monthlyPrice * 12) - yearlyPrice
            : 0;
          const yearlySavingsPercent = monthlyPrice > 0 && yearlyPrice > 0
            ? Math.round((yearlySavings / (monthlyPrice * 12)) * 100)
            : 0;
          const monthlyPending = pendingPlusBillingPeriod === "monthly";
          const yearlyPending = pendingPlusBillingPeriod === "yearly";
          return (
            <div className="space-y-4">
              {cancelError && (
                <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-3 py-2">
                  <p className={`${typography.bodySmall} text-red-700 dark:text-red-300`}>{cancelError}</p>
                </div>
              )}

              <p className={`${typography.body} ${textColors.secondary}`}>
                How would you like to be billed for <span className={`${textColors.primary} font-semibold`}>Plus</span>?
              </p>

              <div className="space-y-3">
                <button
                  type="button"
                  disabled={cancelLoading}
                  onClick={() => handleDowngradeToPlus("monthly")}
                  aria-label="Switch to Plus Monthly"
                  className={`w-full text-left rounded-xl border p-4 transition-colors ${borders.default} ${surfaces.primary} ${surfaces.hover} hover:border-blue-500 dark:hover:border-blue-500 ${cancelLoading ? "opacity-70 cursor-not-allowed" : ""} ${monthlyPending ? "border-blue-500 dark:border-blue-500" : ""}`}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="flex flex-col">
                      <span className={`${typography.h5} ${textColors.primary}`}>Plus Monthly</span>
                      <span className={`${typography.caption} ${textColors.tertiary}`}>Billed monthly</span>
                    </div>
                    <div className="text-right">
                      <span className={`${typography.h4} ${textColors.primary}`}>${monthlyPrice.toFixed(2)}</span>
                      <span className={`${typography.bodySmall} ${textColors.muted}`}>/mo</span>
                    </div>
                  </div>
                  {monthlyPending && (
                    <div className={`mt-2 ${typography.caption} ${textColors.tertiary}`}>Scheduling switch&hellip;</div>
                  )}
                </button>

                <button
                  type="button"
                  disabled={cancelLoading}
                  onClick={() => handleDowngradeToPlus("yearly")}
                  aria-label="Switch to Plus Annual"
                  className={`relative w-full text-left rounded-xl border p-4 transition-colors border-blue-500 dark:border-blue-500 ${surfaces.primary} hover:border-blue-600 dark:hover:border-blue-400 ${cancelLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                >
                  {yearlySavingsPercent > 0 && (
                    <span className="absolute -top-2 right-3 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-600 text-white">
                      Save {yearlySavingsPercent}%
                    </span>
                  )}
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="flex flex-col">
                      <span className={`${typography.h5} ${textColors.primary}`}>Plus Annual</span>
                      <span className={`${typography.caption} ${textColors.tertiary}`}>
                        {yearlyMonthlyEquivalent > 0
                          ? `$${yearlyMonthlyEquivalent.toFixed(2)}/mo, billed annually`
                          : "Billed annually"}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className={`${typography.h4} ${textColors.primary}`}>${yearlyPrice.toFixed(2)}</span>
                      <span className={`${typography.bodySmall} ${textColors.muted}`}>/yr</span>
                    </div>
                  </div>
                  {yearlyPending && (
                    <div className={`mt-2 ${typography.caption} ${textColors.tertiary}`}>Scheduling switch&hellip;</div>
                  )}
                </button>
              </div>

              <p className={`${typography.bodySmall} ${textColors.tertiary}`}>
                The switch takes effect at your next billing cycle — you keep Pro until then.
              </p>
            </div>
          );
        })() : cancelStage === "done" ? (
          <div className="space-y-5">
            <div className="flex items-start gap-3">
              <div className={`mt-0.5 ${textColors.tertiary}`}>
                <SadFaceIcon className="w-6 h-6" />
              </div>
              <div className="space-y-1">
                <div className={`${typography.h5} ${textColors.primary}`}>We are sad to see you go</div>
                <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                  Your subscription will not renew.
                  <br />
                  You’ll keep access until{" "}
                  <span className={`${textColors.primary} font-medium`}>
                    {formatDateShort(currentSubscription?.current_period_end) || "the end of your billing period"}
                  </span>
                  .
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div className={`${typography.bodySmall} ${textColors.primary}`}>What was the reason for your cancellation?</div>

              <div className="space-y-2">
                {CANCELLATION_REASONS.map((label) => (
                  <button
                    key={label}
                    type="button"
                    disabled={cancelFeedbackLoading || cancelFeedbackSent}
                    onClick={() => setCancelReason(label)}
                    className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${
                      cancelReason === label
                        ? `border-blue-500 dark:border-blue-500 ${surfaces.active}`
                        : `${borders.default} ${surfaces.primary} ${surfaces.hover}`
                    } ${cancelFeedbackSent ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    <span className={`${typography.bodySmall} ${textColors.secondary}`}>{label}</span>
                  </button>
                ))}

                <button
                  type="button"
                  disabled={cancelFeedbackLoading || cancelFeedbackSent}
                  onClick={() => setCancelReason("other")}
                  className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${
                    cancelReason === "other"
                      ? `border-blue-500 dark:border-blue-500 ${surfaces.active}`
                      : `${borders.default} ${surfaces.primary} ${surfaces.hover}`
                  } ${cancelFeedbackSent ? "opacity-70 cursor-not-allowed" : ""}`}
                >
                  <span className={`${typography.bodySmall} ${textColors.secondary}`}>Other</span>
                </button>
              </div>

              {cancelReason === "other" && !cancelFeedbackSent && (
                <TextArea
                  value={cancelOtherText}
                  onChange={(e) => setCancelOtherText(e.target.value)}
                  rows={4}
                  placeholder="Tell us what we could do better…"
                />
              )}

              {cancelReason && !cancelFeedbackSent && (
                <div className="flex items-center justify-end">
                  <PrimaryButton
                    onClick={() => {
                      const msg = cancelReason === "other" ? cancelOtherText : cancelReason;
                      submitCancellationFeedback({ message: msg });
                      setRetentionError(null);
                      setCancelStage("retention");
                    }}
                    loading={cancelFeedbackLoading}
                    disabled={cancelReason === "other" && !String(cancelOtherText || "").trim()}
                  >
                    Submit
                  </PrimaryButton>
                </div>
              )}

              {cancelFeedbackError && (
                <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3">
                  <p className={`${typography.bodySmall} text-red-700 dark:text-red-300`}>{cancelFeedbackError}</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className={`${typography.body} ${textColors.secondary}`}>
              Are you sure you want to cancel your subscription?
            </p>
            <p className={`${typography.bodySmall} ${textColors.tertiary}`}>
              You’ll keep access until{" "}
              <span className={`${textColors.primary} font-medium`}>
                {formatDateShort(currentSubscription?.current_period_end) || "the end of your billing period"}
              </span>
              .
            </p>
            {upcomingSubscription && (
              <p className={`${typography.bodySmall} ${textColors.tertiary}`}>
                Your scheduled plan change to{" "}
                <span className={`${textColors.primary} font-medium`}>
                  {upcomingSubscription.plan_display_name || upcomingSubscription.plan?.toUpperCase()}
                </span>{" "}
                will also be cancelled.
              </p>
            )}
            {cancelError && (
              <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3">
                <p className={`${typography.bodySmall} text-red-700 dark:text-red-300`}>{cancelError}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Detailed Feature Comparison */}
      <FeatureComparison 
        plans={plans}
      />

      <ConfettiOverlay isActive={showConfetti} />

      <Modal
        isOpen={successModalOpen}
        onClose={() => {
          setSuccessModalOpen(false);
          setShowConfetti(false);
        }}
        title="You're all set!"
        size="md"
        closeOnOverlayClick
        closeOnEsc
        showCloseButton
      >
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-green-400 text-white flex items-center justify-center">
              <svg className="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <div className="space-y-2">
            <p className={`${typography.body} ${textColors.primary}`}>
              Your <span className="font-semibold">{(successPlan?.planName || "").toUpperCase()}</span>{" "}
              {successPlan?.billingPeriod === "yearly" ? "(Annual)" : "(Monthly)"} plan is now active!
            </p>
            <p className={`${typography.bodySmall} ${textColors.secondary}`}>
              Enjoy your upgraded features. You can manage your subscription anytime from your account settings.
            </p>
          </div>
          <PrimaryButton
            fullWidth
            onClick={() => {
              setSuccessModalOpen(false);
              setShowConfetti(false);
            }}
          >
            Done
          </PrimaryButton>
        </div>
      </Modal>
    </div>
  );
};

export default SubscriptionSettings; 